import { XMarkIcon } from "@heroicons/react/24/outline";
import { ICommunity } from "@sp-crm/core";
import { getMarkerIconColorFromContractStatus } from "components/map/map-common";
import { NextgenStaticMap } from "components/map/nextgen-static";
import { AutosavingInput } from "components/ui/autosaving-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Rating } from "components/ui/rating";
import React, { useCallback } from "react";
import { Thumbnail } from "./thumbnail";
import { ThumbnailUpload } from "./thumbnail-upload";

interface CommunityActionBarProps {
    community: ICommunity;
    onFieldChange: (fieldName: string, newValue: string | number | null) => void;
    onReloadData: () => void;
}

export const CommunityActionBar: React.FC<CommunityActionBarProps> = ({
    community,
    onFieldChange,
    onReloadData,
}) => {
    const clearRating = useCallback(async (): Promise<void> => {
        const result = await fancyConfirm(
            "Clear rating?",
            "Would you like to clear the rating for this community?",
            "Yes, clear",
            "Cancel",
        );
        if (result) {
            onFieldChange("rating", null);
        }
    }, [onFieldChange]);

    return (
        <div className="action-bar p-4">
            <div className="flex flex-col gap-4">
                <div className="flex flex-row flex-wrap items-start justify-center sm:justify-start gap-4 sm:gap-8">
                    <div className="flex-shrink-0">
                        <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                            <div className="w-32 h-32 sm:w-36 sm:h-36 overflow-hidden rounded-lg shadow-sm">
                                <Thumbnail
                                    community={community}
                                    size="128"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <ThumbnailUpload
                                communityId={community.id}
                                reloadCommunity={onReloadData}
                            />
                        </div>
                    </div>

                    <div className="flex-shrink-0 w-64 h-32 sm:w-72 sm:h-36 rounded-lg overflow-hidden shadow-sm">
                        <NextgenStaticMap
                            entityId={community.id}
                            location={community.location}
                            iconType={getMarkerIconColorFromContractStatus(
                                community.contractStatus,
                            )}
                            hideControls={true}
                            grayscale={true}
                            zoom={11}
                        />
                    </div>

                    <div className="flex basis-full sm:basis-auto sm:flex-1 sm:min-w-0">
                        <div className="flex flex-col w-full space-y-2">
                            <AutosavingInput
                                multiLine={true}
                                autoGrow={false}
                                rows={5}
                                initial={community.summary}
                                onCommit={newVal => onFieldChange("summary", newVal)}
                                placeholder="Summary Notes"
                            />
                            <div className="flex items-center space-x-2">
                                <Rating
                                    rating={community.rating ?? 0}
                                    onChange={newRating => {
                                        onFieldChange("rating", newRating);
                                    }}
                                />
                                {community.rating && community.rating > 0 ? (
                                    <span className="flex items-center space-x-1 text-xs text-gray-600">
                                        <span>
                                            {community.rating}{" "}
                                            {community.rating === 1 ? "star" : "stars"}
                                        </span>
                                        <button
                                            onClick={clearRating}
                                            aria-label="Clear rating">
                                            <XMarkIcon className="w-3 h-3 text-gray-500 hover:text-gray-700 bg-gray-300 hover:bg-gray-400 hover:text-gray-900 rounded-full p-0.5" />
                                        </button>
                                    </span>
                                ) : (
                                    <span className="text-xs text-gray-600">Unrated</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
